import { Modal, message } from '@surecompanies/core_components'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import { PresignedUploadUrlDto } from './dto/presigned-upload-url.dto'
import { TerminationFileRowDto } from './dto/termination-file-row.dto'
import { TerminationFilesResponseDto } from './dto/termination-files-response.dto'
import { TerminationFileSummaryDto } from './dto/termination-summary.dto'

export interface GetTerminationFilesParams {
  clientId?: string
  page?: number
  limit?: number
}

export const getTerminationFiles = async (
  params: GetTerminationFilesParams = {}
): Promise<TerminationFilesResponseDto> => {
  const response = await apiClient.get<TerminationFilesResponseDto>(
    '/terminations',
    { params }
  )
  return response.data
}

export const getPresignedUploadUrl = async ({
  clientPublicKey,
  fileName,
}: {
  clientPublicKey: string | undefined
  fileName: string
}): Promise<PresignedUploadUrlDto> => {
  const url = '/terminations/presigned-upload-url'
  const ret = await apiClient.post<PresignedUploadUrlDto>(url, {
    clientPublicKey: clientPublicKey,
    fileName,
  })
  return ret.data
}

export const uploadTerminationFile = async (presignedUrl: string, file) => {
  if (!file) {
    message.error('No file selected')
    return false
  }
  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'X-Test': 'trigger-preflight', // this forces OPTIONS to appear
      },
      body: file,
    })

    if (response.ok) {
      return true
    } else {
      const errorText = await response.text()
      console.error('Upload failed:', response.status, errorText)
    }
  } catch (error: any) {
    message.error('Error uploading file: ', error?.message)
    console.error('Upload failed:', error.message)
  }
  return false
}

export const createTerminationFileFromS3 = async ({
  fileName,
  importPublicKey,
  clientPublicKey,
}: {
  fileName: string
  importPublicKey: string
  clientPublicKey: string
}) => {
  try {
    const ret = await apiClient.post('/terminations', {
      fileName,
      importPublicKey,
      clientPublicKey,
    })
    if (ret.data.error) {
      message.error(ret.data.message)
    } else {
      Modal.success({
        title: 'Terminations Submitted',
        content:
          'Terminations successfully submitted! Changes will be reviewed by a SureCo Administrator and reflected within 1 business day.',
        centered: true,
      })
      return ret.data
    }
  } catch (error: any) {
    const messageText =
      error.response?.data?.message || error.message || 'Unknown error occurred'
    console.error('Error:', messageText)
    message.error(messageText, 10)

    return { error: true, message: messageText }
  }
}

export type TerminationFileDetailDto = {
  fileName: string
  status: string
  uploadedAt: string
  rowCount: number
  clientPublicKey: string
  errors?: string | null
  rows: TerminationFileRowDto[]
}

export const getTerminationFileByPublicKey = async (
  publicKey: string
): Promise<TerminationFileDetailDto> => {
  const url = `/terminations/${publicKey}`
  const res = await apiClient.get<TerminationFileDetailDto>(url)
  return res.data
}

export const getTerminationFileSummaryByPublicKey = async (
  publicKey: string
): Promise<TerminationFileSummaryDto> => {
  const url = `/terminations/${publicKey}`
  const res = await apiClient.get(url)
  return res.data
}

export const downloadFile = async (
  publicKey: string,
  fileName: string
): Promise<void> => {
  try {
    const url = `/terminations/${publicKey}/presigned-download-url`
    const res = await apiClient.get(url)

    const a = document.createElement('a')
    a.href = res.data.url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } catch (error) {
    console.error('File download failed:', error)
  }
}

export const fetchFileContent = async (
  publicKey: string
): Promise<{ fileName: string; csvText: string }> => {
  try {
    const url = `/terminations/${publicKey}/presigned-download-url`
    const res = await apiClient.get(url)

    const downloadUrl = res.data.url
    const fileName = res.data.fileName ?? 'termination_file.csv'

    const response = await fetch(downloadUrl)
    const csvText = await response.text()

    return { fileName, csvText }
  } catch (error) {
    console.error('Failed to fetch file content:', error)
    throw error
  }
}
